import { EMPTY_STRING, EBooleanStatus } from 'commons-ui';
import { BOOLEAN_VALUES } from 'commons-ui/src/constants';
import { ICsvRow, IParseCsvResult, IInvalidCsvRow } from '../types';
import { CSV_ROW_LIMIT, CSV_HEADERS, CSV_ACTIONS, MESSAGES } from '../constants';

const normalise = (str: string): string => str.replace(/\s|_/g, '').toLowerCase();

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (email: string): boolean => EMAIL_REGEX.test(email);

export const parseCsv = (file: File): Promise<IParseCsvResult> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onload = (e) => {
			try {
				const text = (e.target?.result as string) ?? '';
				const lines = text.split('\n').filter((line) => line.trim());

				if (lines.length === 0) {
					throw new Error(MESSAGES.VALIDATION.CSV_EMPTY);
				}

				const rawHeaderCells = lines[0].split(',').map((h) => h.trim());
				const headerCells = rawHeaderCells.map(normalise);

				const expectedHeaders = [
					normalise(CSV_HEADERS.EMAIL),
					normalise(CSV_HEADERS.PERMISSION_NAME),
					normalise(CSV_HEADERS.ACTION),
					normalise(CSV_HEADERS.EXPIRE_AFTER_SEC),
					normalise(CSV_HEADERS.IS_MASKED)
				];

				const isHeaderValid: boolean =
					headerCells.length === expectedHeaders.length && expectedHeaders.every((h, idx) => headerCells[idx] === h);
				if (!isHeaderValid) {
					throw new Error(MESSAGES.VALIDATION.CSV_INVALID_HEADERS);
				}

				const rows: ICsvRow[] = [];
				const invalidRows: IInvalidCsvRow[] = [];
				for (let i = 1; i < lines.length; i += 1) {
					const values = lines[i].split(',').map((v) => v.trim());

					const [
						email = EMPTY_STRING,
						permission = EMPTY_STRING,
						action = EMPTY_STRING,
						expireAfterSec = EMPTY_STRING,
						isMaskedRaw = EMPTY_STRING
					] = values;

					const emailValid = email && isValidEmail(email);

					const actionLower = action.toLowerCase();
					const isMaskedLower = isMaskedRaw.toLowerCase();

					const isActionValid =
						actionLower === CSV_ACTIONS.ASSIGN.toLowerCase() || actionLower === CSV_ACTIONS.REMOVE.toLowerCase();

					const isExpireAfterValid = !Number.isNaN(parseInt(expireAfterSec, 10)) || expireAfterSec === EMPTY_STRING;

					const isMaskedValid =
						isMaskedRaw === EMPTY_STRING ||
						isMaskedLower === BOOLEAN_VALUES.TRUE ||
						isMaskedLower === BOOLEAN_VALUES.FALSE;

					if (emailValid && permission && isActionValid && isExpireAfterValid && isMaskedValid) {
						rows.push({
							email,
							permission,
							action: (actionLower === CSV_ACTIONS.ASSIGN.toLowerCase()
								? CSV_ACTIONS.ASSIGN
								: CSV_ACTIONS.REMOVE) as keyof typeof CSV_ACTIONS,
							expireAfter: expireAfterSec ? parseInt(expireAfterSec, 10) : undefined,
							isMasked: isMaskedRaw ? isMaskedLower === BOOLEAN_VALUES.TRUE : undefined
						});
					} else {
						let reason: string = MESSAGES.INVALID_ROW_REASONS.MISSING_REQUIRED_FIELDS;
						if (!emailValid) {
							reason = MESSAGES.INVALID_ROW_REASONS.INVALID_EMAIL;
						} else if (!isActionValid) {
							reason = MESSAGES.INVALID_ROW_REASONS.INVALID_ACTION;
						} else if (!isExpireAfterValid) {
							reason = MESSAGES.INVALID_ROW_REASONS.INVALID_EXPIRE_AFTER;
						} else if (!isMaskedValid) {
							reason = MESSAGES.INVALID_ROW_REASONS.INVALID_IS_MASKED;
						}
						invalidRows.push({ index: i + 1, reason });
					}
				}

				if (rows.length === 0) {
					throw new Error(MESSAGES.VALIDATION.CSV_NO_DATA);
				}

				if (rows.length > CSV_ROW_LIMIT) {
					throw new Error(MESSAGES.VALIDATION.CSV_ROW_LIMIT_EXCEEDED);
				}

				resolve({ validRows: rows, invalidRows });
			} catch (err) {
				reject(err instanceof Error ? err : new Error(String(err)));
			}
		};

		reader.onerror = () => reject(new Error(MESSAGES.VALIDATION.FILE_READ_ERROR));
		reader.readAsText(file);
	});

export const formatIsMasked = (value?: boolean): string => {
	if (value === undefined) return '-';
	return value ? EBooleanStatus.YES : EBooleanStatus.NO;
};
